// Field help for the quota screens (SCR-230 list and editor, SCR-231 standing, grants and events;
// REQ-080..082, REQ-087, REQ-094). Keys are the ids passed to <Field help="…"> and the column
// headers; every rendered label must have one (tests/unit/help.test.ts).
import type { HelpTopic } from "../topic";

export const QUOTAS_TOPICS = {
  "quotas.name": {
    title: "Name",
    body: "What people call this cap. It names the cap in reports, in the event history and in the message a refused developer sees. At most 100 characters.",
    example: "“Platform team monthly spend” or “Per-developer daily tokens”.",
    article: "users-teams-and-sign-in",
    section: "users-and-teams",
  },
  "quotas.scope": {
    title: "Applies to",
    body: "What the cap counts. The whole organization, one team or one person is one total; each member of the organization or of a team gets the cap separately. A person's own cap overrides a team's per-member cap, which overrides the organization's. Every cap that applies is checked, and the tightest one refuses first.",
    example: "Each member of team Platform at $100 a month, plus team Platform at $1,000 a month, plus Dana at $150: Dana is refused at $150 unless the team reaches $1,000 first.",
    article: "users-teams-and-sign-in",
    section: "users-and-teams",
  },
  "quotas.target": {
    title: "Team or person",
    body: "The team or the person the cap is for. The lists hold the organization's teams and its first 500 people.",
    example: "Pick team Platform for “Each member of a team”.",
    article: "users-teams-and-sign-in",
    section: "users-and-teams",
  },
  "quotas.metric": {
    title: "Measure",
    body: "What is counted: spend in dollars, tokens (input, output and cache writes; cache reads are not counted) or requests admitted.",
    example: "Tokens at 2,000,000 a day caps each developer's daily token use whatever the model costs.",
    article: "pricing-and-markup",
    section: "tokens-to-invoice",
  },
  "quotas.basis": {
    title: "Spend basis",
    body: "Which dollars a spend cap counts: the organization's own price (its rate plan), or the AWS list cost. AWS list cost is for AllCode's guardrail quotas only.",
    example: "A $5,000 guardrail at AWS list cost stops an AllCode-paid account before its AWS bill passes $5,000.",
    article: "pricing-and-markup",
    section: "cost-basis",
  },
  "quotas.period": {
    title: "Period",
    body: "How often the count starts over: each UTC day, each week from Monday 00:00 UTC, or each calendar month in UTC.",
    example: "A monthly cap resets on the 1st at 00:00 UTC.",
    article: "glossary",
  },
  "quotas.limit": {
    title: "Limit",
    body: "The cap in the measure: dollars for spend (up to six decimals), a whole number for tokens and requests. Lowering it below what is already used refuses the next request at once; the preview shows who that affects.",
    example: "100 on a monthly spend cap is $100.00 a month.",
    article: "pricing-and-markup",
  },
  "quotas.thresholds": {
    title: "Warn at",
    body: "Percentages of the limit at which the cap warns once per period: developers see the warning in Claude Code and Codex, and webhooks are sent. Up to five, rising, each between 0.01 and 99.99. Leave it empty for no warnings.",
    example: "75, 95 warns at three quarters and again just before the limit.",
    article: "claude-code-and-codex",
  },
  "quotas.enforcement": {
    title: "When reached",
    body: "Block refuses requests once the limit (and any grace) is used up; Warn only signals and reports, and never refuses.",
    example: "Start a new cap at Warn only for a month to see who it would affect, then switch it to Block.",
    article: "claude-code-and-codex",
  },
  "quotas.grace": {
    title: "Grace",
    body: "How far past the limit a blocking cap still admits requests, as a percentage of the limit (0 to 50). 0 refuses at exactly 100 %.",
    example: "10 on a $100 cap admits requests until $110 is used.",
    article: "glossary",
  },
  "quotas.failMode": {
    title: "If the proxy cannot check",
    body: "What a proxy does when it cannot learn how much has been used (the Nexus API is unreachable): Admit lets requests through, Refuse turns them away. Admit is the default.",
    example: "Refuse suits a hard budget where overspending is worse than a short outage.",
    article: "what-nexus-does",
  },
  "quotas.owner": {
    title: "Owner",
    body: "Whose cap it is. Organization caps are the customer admins' to manage; a guardrail is AllCode's, read-only to the customer.",
    example: "A guardrail at AWS list cost on an account AllCode pays for.",
    article: "what-nexus-does",
  },
  "quotas.status": {
    title: "Status",
    body: "Active caps are counted and enforced. A disabled cap is kept with its history but checks nothing until it is enabled again.",
    example: "Disable a cap over a hackathon week instead of deleting it.",
    article: "glossary",
  },
  "quotas.utilization": {
    title: "Used",
    body: "How much of the limit (with any active increases) this period has used. Where dollar figures are hidden from you, only the percentage shows.",
    example: "$42.00 of $100.00 is 42%.",
    article: "glossary",
  },
  "quotas.state": {
    title: "State",
    body: "OK below the first warning; Warning past one; In grace between the limit and the grace; Exhausted when requests are refused; Exempt while a grant lifts the cap.",
    example: "A member at 97 % of a cap that warns at 75 and 95 shows Warning.",
    article: "glossary",
  },
  "quotas.grantKind": {
    title: "Grant",
    body: "A temporary change that never edits the cap: an increase adds an amount to the limit, an exemption lifts refusal altogether, until the grant expires or is revoked.",
    example: "+$50 for three days lets a blocked developer finish a release.",
    article: "glossary",
  },
  "quotas.grantAmount": {
    title: "Increase by",
    body: "What the grant adds to the limit, in the cap's measure: dollars for spend, a whole number for tokens and requests.",
    example: "50 on a spend cap adds $50.00.",
    article: "glossary",
  },
  "quotas.grantMember": {
    title: "For",
    body: "On a per-member cap, a grant can cover one person or every member at once.",
    example: "Pick Dana to raise only Dana's cap.",
    article: "users-teams-and-sign-in",
    section: "users-and-teams",
  },
  "quotas.grantDays": {
    title: "Days",
    body: "How long the grant lasts from now, in whole days, at most 31.",
    example: "3 ends the grant three days from now, at the same time of day.",
    article: "glossary",
  },
  "quotas.grantReason": {
    title: "Reason",
    body: "Why the grant is given, 10 to 500 characters. It is recorded in the audit log.",
    example: "“Release week for the payments service; ticket OPS-1234.”",
    article: "audit-log",
  },
  "quotas.preview": {
    title: "Preview",
    body: "What this cap would have done over the last complete period: how many people (or teams) would have passed each warning and the limit, and on which day. Nothing is saved or audited.",
    example: "3 of 12 members would have reached the $100 limit last month, the first on 18 Sep.",
    article: "glossary",
  },
  "quotas.fleetFullest": {
    title: "Fullest quota",
    body: "The organization's quota nearest its limit right now: which quota, for whom, how much of it is used and its state. Organizations are ranked by it, so whoever is closest to being refused is at the top.",
    example: "82% · Members monthly · Dana Lee means Dana has used 82% of a per-member monthly cap.",
    article: "glossary",
  },
  "quotas.fleetTile": {
    title: "Quotas",
    body: "How many organizations have a quota exhausted (refusing), in grace (past the limit but still served for a short while) or past a warning right now. Open quota standing for the full list.",
    example: "Exhausted 2 orgs means two organizations each have at least one person, team or org total being refused.",
    article: "glossary",
  },
  "notifications.webhooks": {
    title: "Webhooks",
    body: "Addresses Nexus posts to when a quota passes a warning, reaches its limit or gets a grant. Each event is sent once per quota, period and threshold, retried for 24 hours. An organization may have up to 10.",
    example: "One Slack webhook for the platform team's channel and one generic webhook for your on-call tool.",
    article: "audit-log",
  },
  "notifications.name": {
    title: "Name",
    body: "What people call this webhook in the list. At most 100 characters.",
    example: "“#platform-alerts in Slack”.",
    article: "glossary",
  },
  "notifications.url": {
    title: "URL",
    body: "Where events are posted: https only, no user name or password, and a public address — private, loopback, link-local and cloud metadata addresses are refused, and redirects are not followed. The URL is stored encrypted and never shown again; only its host is.",
    example: "https://hooks.slack.com/services/T000/B000/XXXX for a Slack incoming webhook.",
    article: "glossary",
  },
  "notifications.format": {
    title: "Format",
    body: "Generic JSON posts the event as JSON signed with X-Nexus-Signature (HMAC-SHA256 with the webhook's secret). Slack posts a readable message to a Slack incoming webhook.",
    example: "Pick Slack for a hooks.slack.com URL, Generic JSON for your own service.",
    article: "glossary",
  },
  "notifications.events": {
    title: "Events",
    body: "Which quota events this webhook receives: a warning threshold crossed, a limit reached (requests refused), or a grant added.",
    example: "Only “Limit reached” for a paging tool; all three for a team channel.",
    article: "glossary",
  },
  "notifications.owner": {
    title: "Owner",
    body: "An organization webhook belongs to the customer's admins. An AllCode webhook is AllCode's own, for guardrail alerts; only a super-admin creates or changes one, and the customer sees it read-only.",
    example: "AllCode for a webhook to AllCode's operations channel on an account AllCode pays for.",
    article: "signing-in-and-roles",
  },
  "notifications.status": {
    title: "Status",
    body: "Active webhooks receive events; disabled ones receive none. Failing means every delivery has failed for 24 hours: events are still queued, and enabling it again or a successful test clears it.",
    example: "Failing after the Slack channel was archived; edit the URL, then send a test.",
    article: "glossary",
  },
  "notifications.consecutiveFailures": {
    title: "Failures in a row",
    body: "How many delivery attempts have failed since the last success. A new URL or a delivered event resets it to 0.",
    example: "12 after a receiver outage overnight.",
    article: "glossary",
  },
  "notifications.deliveryStatus": {
    title: "Status",
    body: "Delivered: the receiver answered 2xx. Retrying: the last attempt failed and another is scheduled, with growing gaps, for up to 24 hours. Gave up: 24 hours passed without a success.",
    example: "Retrying, next try 14:05 UTC, after the receiver answered 503.",
    article: "glossary",
  },
  "notifications.attempts": {
    title: "Attempts",
    body: "How many times this event has been posted to the webhook so far, including the successful one.",
    example: "3: two failures, then delivered.",
    article: "glossary",
  },
  "notifications.errorClass": {
    title: "Last error",
    body: "Why the last attempt failed, without any content: timed out, could not connect, address refused (it resolved to a private or metadata address), refused by the receiver (4xx), receiver error (5xx) or TLS failure.",
    example: "Receiver error (5xx) when the receiver was down for maintenance.",
    article: "glossary",
  },
} satisfies Record<string, HelpTopic>;
